"use client";

import Link from "next/link";
import { useState, useRef, useEffect } from "react";
import { type Locale, localizePath } from "@/lib/i18n/config";
import { getDictionary } from "@/lib/i18n/dictionaries";

type Props = { locale: Locale };

export function BilMegaMenu({ locale }: Props) {
  const dict = getDictionary(locale);
  const t = dict.megaBil;
  const nav = dict.nav;
  const link = (p: string) => localizePath(p, locale);
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLLIElement>(null);

  // Lille delay på close så hover ikke "blinker" når musen krydser gap
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const openNow = () => {
    cancelClose();
    setOpen(true);
  };

  // Luk på Escape + klik udenfor
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const Column = ({
    heading,
    items,
  }: {
    heading: string;
    items: { title: string; desc: string; href: string }[];
  }) => (
    <div>
      <h3 className="text-[11px] font-bold uppercase tracking-[0.14em] text-[var(--color-primary-700)] pb-2.5 mb-3 border-b border-[var(--color-line-soft)]">
        {heading}
      </h3>
      <ul className="flex flex-col gap-3">
        {items.map((item) => {
          // Eksplicit sprog-link (fx /en/personbil) må IKKE localizes — ellers
          // stripper localizePath /en-præfikset og sender til den danske side.
          const href = item.href.startsWith("/en/") ? item.href : link(item.href);
          return (
          <li key={item.title}>
            <Link
              href={href}
              className="block group"
              onClick={() => setOpen(false)}
            >
              <span className="block text-[15px] font-bold text-[var(--color-navy-700)] group-hover:text-[var(--color-primary-700)] leading-tight">
                {item.title}
              </span>
              <span className="block text-[12.5px] text-[var(--color-muted)] leading-tight mt-0.5">
                {item.desc}
              </span>
            </Link>
          </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <li
      ref={wrapperRef}
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={scheduleClose}
      onFocus={openNow}
      onBlur={(e) => {
        // Luk hvis fokus forlader hele wrapper
        if (!wrapperRef.current?.contains(e.relatedTarget as Node)) {
          scheduleClose();
        }
      }}
    >
      <Link
        href={link("/personbil")}
        aria-haspopup="true"
        aria-expanded={open}
        className="px-2.5 py-2 hover:text-[var(--color-primary-700)] inline-flex items-center gap-1"
      >
        {nav.bil}
      </Link>

      {/* Mega-panel — vi bruger IKKE role="menu" fordi det kræver
          role="menuitem" children. Vores indhold er bare en gruppe af
          navigation-links, så aria-label på containeren er nok.
          inert sikrer at links er utilgængelige for tastatur/AT når lukket. */}
      <div
        aria-label={nav.bil}
        {...(!open ? { inert: "" as unknown as boolean } : {})}
        className="bil-mega-panel absolute left-0 top-full pt-3"
        style={{
          opacity: open ? 1 : 0,
          transform: open ? "translateY(0)" : "translateY(8px)",
          transition: "opacity 180ms ease, transform 180ms ease",
          pointerEvents: open ? "auto" : "none",
        }}
      >
        <div className="bil-mega-card bg-white border border-[var(--color-line)] rounded-[14px] shadow-[0_18px_40px_rgba(0,23,37,.12)] p-7">
          <Column heading={t.col1Heading} items={t.col1 as { title: string; desc: string; href: string }[]} />
          <Column heading={t.col2Heading} items={t.col2 as { title: string; desc: string; href: string }[]} />
          <Column heading={t.col3Heading} items={t.col3 as { title: string; desc: string; href: string }[]} />

          {/* Promo-kort — skjules når der ikke er plads (under 1240px viewport) */}
          <div
            className="bil-mega-promo rounded-[12px] p-5 flex flex-col"
            style={{
              background: "var(--color-green-soft)",
              border: "1px solid rgba(118,193,105,0.28)",
            }}
          >
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-[var(--color-primary-700)] mb-2">
              {t.promoKicker}
            </div>
            <p className="text-[13px] leading-[1.5] text-[var(--color-navy-700)] mb-4 flex-1">
              {t.promoBody}
            </p>
            <Link
              href={link(t.promoHref)}
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center bg-[var(--color-primary-700)] hover:bg-[var(--color-primary-800)] text-white px-4 py-2.5 rounded-[9px] text-[13.5px] font-semibold transition-colors green-text-shadow"
              style={{ boxShadow: "0 6px 14px rgba(118,193,105,0.3)" }}
            >
              {t.promoCta}
            </Link>
          </div>
        </div>
      </div>
    </li>
  );
}
